/**
 * Why mail cannot leave from an attached domain yet, as the sending provider reports it: one code per condition still
 * unmet, recorded on the domain each time it is checked. `dkim_*` and `mail_from_*` carry the provider's own status,
 * lower case (`dkim_pending`, `mail_from_failed`); `provider_error:<name>` is a check the provider refused to answer.
 */
export type DomainSendingWait = string

const PLAIN: Readonly<Record<string, string>> = {
    sending_off: 'Sending from attached domains is turned off on this deployment of Zero Human.',
    sending_not_configured: 'Sending from attached domains is not fully configured on this deployment of Zero Human.',
    identity_missing: 'The sending provider has no record of this domain yet. It is set up on the next check.',
    dkim_signing_off: 'The sending provider is not signing mail for this domain with its Zero Human keys.',
    configuration_set_mismatch: 'The sending provider has this domain on the wrong delivery settings.',
    mail_from_missing: 'The sending provider has no bounce address (bounces.<domain>) for this domain.',
    identity_not_verified: 'The sending provider has not verified this domain for sending yet.',
    account_sending_paused: 'Zero Human’s sending account is paused by the sending provider.',
    account_in_sandbox:
        'Zero Human’s sending account is still waiting for production access from the sending provider, so it can only send to verified addresses.',
}

const STATUS: Readonly<Record<string, string>> = {
    pending: 'is still being verified',
    not_started: 'has not been checked yet',
    temporary_failure: 'could not be checked this time; it is tried again',
    failed: 'failed verification: check the published records',
}

/** One code in plain words, for the Domains page and anybody reading the domain over the API. */
export function domainSendingWaitText(code: DomainSendingWait, domain = '<domain>'): string {
    if (code.startsWith('provider_error:')) {
        return `The sending provider did not answer the check (${code.slice('provider_error:'.length)}). It is tried again every minute.`
    }
    for (const [prefix, what] of [
        ['dkim_', 'DKIM signing for this domain'],
        ['mail_from_', `The bounce address bounces.${domain}`],
    ] as const) {
        if (code.startsWith(prefix) && STATUS[code.slice(prefix.length)]) {
            return `${what} ${STATUS[code.slice(prefix.length)]}.`
        }
    }
    return (PLAIN[code] ?? code).replace('<domain>', domain)
}
